<template>
	<div
		class="time-off-list-popover-skeleton"
		:aria-label="$t('spa__general.loading')"
	>
		<div
			v-for="index in skeletonItemCount"
			:key="index"
			class="time-off-list-popover-skeleton__row"
		>
			<div
				v-if="columns.includes(COLUMN_USER)"
				:data-column="COLUMN_USER"
				class="time-off-list-popover-skeleton__items-wrapper"
			>
				<SuiSkeleton
					is-circle
					width="2.5rem"
					class="time-off-list-popover-skeleton__single-item"
				/>
				<div class="time-off-list-popover-skeleton__column-wrapper">
					<SuiSkeleton
						:width="
							index === 1
								? '15rem'
								: index === 2
									? '18.75rem'
									: '10.75rem'
						"
						height="1.75rem"
					/>
					<SuiSkeleton
						:width="index === 1 ? '10rem' : '14rem'"
						:height="SKELETON_ITEM_HEIGHT"
					/>
				</div>
			</div>
			<SuiSkeleton
				v-if="columns.includes(COLUMN_AVAILABILITY)"
				:data-column="COLUMN_AVAILABILITY"
				:width="index === 1 ? '5rem' : '5.5rem'"
				:height="SKELETON_ITEM_HEIGHT"
			/>
			<SuiSkeleton
				v-if="columns.includes(COLUMN_PERIOD)"
				:data-column="COLUMN_PERIOD"
				:width="index === 1 ? '10rem' : '11.375rem'"
				:height="SKELETON_ITEM_HEIGHT"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { SuiSkeleton } from '@developers/scoro-ui';
import { computed } from 'vue';
import {
	COLUMN_AVAILABILITY,
	COLUMN_PERIOD,
	COLUMN_USER,
	type TimeOffPopoverColumn,
} from '@/features/Calendar/components/TimeOff/types';

interface TimeOffListPopoverSkeletonProps {
	usersCount: number;
	columns?: Array<TimeOffPopoverColumn>;
}

const props = withDefaults(defineProps<TimeOffListPopoverSkeletonProps>(), {
	columns: () => [COLUMN_USER, COLUMN_AVAILABILITY, COLUMN_PERIOD],
});

const SKELETON_ITEM_HEIGHT = '1.5rem';
const MAX_LOADING_PLACEHOLDERS = 3;

const skeletonItemCount = computed(() =>
	Math.min(Math.max(props.usersCount, 1), MAX_LOADING_PLACEHOLDERS)
);
</script>

<style lang="scss" scoped>
.time-off-list-popover-skeleton {
	padding: 0 2.5rem;

	&__row {
		display: grid;
		grid-template-columns:
			[user-column] 28rem [availability-column] 10rem [period-column] minmax(
				16rem,
				1fr
			)
			[buttons-column] 6rem;
		gap: 1rem;
		justify-items: end;
		padding: 1.75rem 0;

		[data-column='user'] {
			grid-column-start: user-column;
		}

		[data-column='availability'] {
			grid-column-start: availability-column;
		}

		[data-column='period'] {
			grid-column-start: period-column;
		}

		&:not(:last-of-type) {
			border-bottom: 1px solid $neutral-20;
		}
	}

	&__single-item {
		place-self: start;
	}

	&__items-wrapper {
		display: flex;
		align-items: center;
		justify-self: start;
		gap: 1rem;
	}

	&__column-wrapper {
		display: flex;
		gap: 1rem;
		flex-direction: column;
	}
}
</style>
